import type { ChartReviewResponse, FeedbackRequest } from '@ctw/shared/api/ai/types';
import type { FHIRModel } from '@ctw/shared/api/fhir/models/fhir-model';
import { notifyFromDrawer } from '@ctw/shared/components/toast';
import { FeedbackComponent } from './feedback/feedback.v2';
import { PatientSummaryDrawerFooter } from './patient-summary-drawer-footer';
import './patient-summary-drawer.scss';
import { useDrawer } from '@ctw/shared/context/drawer-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { tw } from '@ctw/shared/utils/tailwind';
import type { Resource } from 'fhir/r4';

type PatientFeedbackDrawerProps = {
  responses: Array<ChartReviewResponse>;
};

export function usePatientSummaryDrawer<T extends Resource, M extends FHIRModel<T>>({
  responses,
}: PatientFeedbackDrawerProps) {
  const telemetry = useTelemetry();
  const { openDrawer, closeDrawer, isDrawerOpen } = useDrawer({
    boundaryName: 'PatientSummaryDrawer',
  });

  return {
    openDrawer: ({
      model,
      feedback,
      setFeedback,
      triggerFeedback,
    }: {
      model?: M;
      feedback: FeedbackRequest;
      setFeedback: (feedback: FeedbackRequest) => void;
      triggerFeedback: () => void;
    }) => {
      // aggregate the hpi and citations from all responses
      // TODO: refactor the feedback component to handle this per card as
      // opposed to per summary response
      const hpi = responses.map((r) => r.hpi).join('\n');
      const citations = responses.flatMap((r) => r.citations);

      const handleFeedbackUpdate = (updatedFeedback: FeedbackRequest) => {
        const newFeedback = {
          ...updatedFeedback,
          content: hpi,
          citations,
        };
        setFeedback(newFeedback);
      };

      handleFeedbackUpdate(feedback);

      const handleFeedbackSubmit = () => {
        notifyFromDrawer({ type: 'success', title: 'Feedback Sent!' });
        void triggerFeedback();
        closeDrawer();
      };

      openDrawer({
        telemetry,
        action: {
          telemetryContext: {
            resourceType: model?.resourceType,
          },
          title: 'Feedback',
          content: {
            body: (
              <div className={tw`space-y-3 p-1`}>
                <FeedbackComponent feedback={feedback} onFeedbackUpdate={handleFeedbackUpdate} />
              </div>
            ),
            footer: (
              <PatientSummaryDrawerFooter
                feedbackEnabled={true}
                onClose={closeDrawer}
                onFeedbackClick={handleFeedbackSubmit}
              />
            ),
          },
        },
      });
    },
    isDrawerOpen,
  };
}
